import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { severityStyle, typeLabel } from "../lib/warnings";
import { RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nProvider";
import WarningCard from "../components/warnings/WarningCard";

const WARNING_TYPES = [
  "booking_overlap", "buffer_violation", "vehicle_unavailable", "incident_impact",
  "missing_fields", "pending_too_long", "return_overdue", "deposit_unresolved",
  "missing_return_inspection", "inspection_damage", "vehicle_document_expiry",
  "location_risk",
];

const SEVERITIES = ["critical", "high", "medium", "low", "info"];

const STATUS_GROUPS = [
  { key: "open", labelKey: "warnings.tab.active", statuses: "active,reviewed" },
  { key: "closed", labelKey: "warnings.tab.closed", statuses: "closed,fixed" },
];

export default function Warnings() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanBusy, setScanBusy] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [tab, setTab] = useState("open");

  const load = async () => {
    setLoading(true);
    try {
      const tabCfg = STATUS_GROUPS.find((g) => g.key === tab) || STATUS_GROUPS[0];
      const params = { status: tabCfg.statuses };
      if (filterType !== "all") params.warning_type = filterType;
      if (filterSeverity !== "all") params.severity = filterSeverity;
      const { data } = await api.get("/warnings", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterType, filterSeverity, tab]);

  const rescan = async () => {
    setScanBusy(true);
    try {
      const { data } = await api.post("/warnings/rescan");
      toast.success(t("warnings.toast.scan_complete")
        .replace("{detected}", data.detected)
        .replace("{fixed}", data.auto_fixed));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScanBusy(false);
    }
  };

  const counts = useMemo(() => {
    const by = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    items.forEach((w) => { by[w.severity] = (by[w.severity] || 0) + 1; });
    return by;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="warnings-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            {t("warnings.page.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {t("warnings.page.subtitle")}
          </p>
        </div>
        <button
          data-testid="warnings-rescan-btn"
          onClick={rescan}
          disabled={scanBusy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-800 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${scanBusy ? "animate-spin" : ""}`} /> {t("warnings.rescan")}
        </button>
      </div>

      {/* Severity summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="warnings-counts">
        {SEVERITIES.map((sev) => {
          const s = severityStyle(sev);
          return (
            <div key={sev} className={`rounded-lg border ${s.banner} p-3`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                {t(`warnings.severity.${sev}`)}
              </div>
              <div className={`text-2xl font-display font-semibold mt-0.5 ${s.title}`} data-testid={`warning-count-${sev}`}>
                {counts[sev] || 0}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters + tabs */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist">
          {STATUS_GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`warnings-tab-${g.key}`}
              onClick={() => setTab(g.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                tab === g.key ? "bg-[#1B4332] text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              {t(g.labelKey)}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select
          data-testid="warnings-filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white text-sm px-3 py-2"
        >
          <option value="all">{t("warnings.filter.all_types")}</option>
          {WARNING_TYPES.map((type) => (
            <option key={type} value={type}>{typeLabel(type, t)}</option>
          ))}
        </select>
        <select
          data-testid="warnings-filter-severity"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white text-sm px-3 py-2"
        >
          <option value="all">{t("warnings.filter.all_severities")}</option>
          {SEVERITIES.map((sev) => (
            <option key={sev} value={sev}>{t(`warnings.severity.${sev}`)}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-sm text-slate-500 p-6 rounded-xl border border-slate-200 bg-white" data-testid="warnings-loading">
            {t("warnings.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center p-10 rounded-xl border border-dashed border-slate-300 bg-white" data-testid="warnings-empty">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
            <div className="mt-3 font-medium text-slate-800">
              {tab === "open" ? t("warnings.empty.active.title") : t("warnings.empty.closed.title")}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {tab === "open" ? t("warnings.empty.active.body") : t("warnings.empty.closed.body")}
            </div>
          </div>
        ) : (
          items.map((w) => (
            <WarningCard key={w.id} w={w} onChanged={load} />
          ))
        )}
      </div>
    </div>
  );
}
